import { useState, useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import StandardTable from '../tables/StandardTable';
import { downloadExcel, downloadPdf, convertAllDates, exportRealtorWishlistWorksheetsPdf } from '../../utils/Functions';
import LoadingLogo from '../common/LoadingLogo';

const Worksheets = () => {
  const project = useSelector(selectProject);
  const [wishlistSummary, setWishlistSummary] = useState<any>(null);
  const [duplicates, setDuplicates] = useState([]);
  const [availableUnitTypes, setAvailableUnitTypes] = useState<string[]>([]);
  const [publicWorksheets, setPublicWorksheets] = useState<any[]>([]);
  const [wishlistWorksheets, setWishlistWorksheets] = useState<any[]>([]);

  const { loading } = useQuery(WORKSHEETS, {
    variables: { project: project._id },
    onCompleted: (data) => {
      setWishlistSummary(data.getWorksheetSummary);
      // eslint-disable-next-line no-console
      console.log('DEBUG wishlistSummary.unitTypes', data.getWorksheetSummary.unitTypes);
    },
  });

  useQuery(DUPLICATES, {
    variables: { project: project._id },
    onCompleted: (data) => {
      setDuplicates(data.getDuplicates);
    },
  });

  useQuery(UNITS, {
    variables: { filter: { project: project._id } },
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const types = [...new Set(data.unitMany.map((unit: any) => unit.unitType))].filter(Boolean) as string[];
      setAvailableUnitTypes(types);
      // eslint-disable-next-line no-console
      console.log('DEBUG availableUnitTypes', types, 'from', data.unitMany.length, 'units');
    },
  });

  useQuery(WISHLIST_WORKSHEETS, {
    variables: { search: '', project: project._id, type: 'wishlist', page: 1, perPage: 10000 },
    onCompleted: (data) => {
      setPublicWorksheets(data.getWorksheets.worksheets.filter((worksheet: any) => !worksheet.realtor));
      setWishlistWorksheets(data.getWorksheets.worksheets);
    },
  });

  let unitTypes = [
    'studio',
    'jrOneBr',
    'oneBr',
    'oneBrInbound',
    'oneBrMedia',
    'oneBrFlex',
    'oneBrDen',
    'oneBrDenInbound',
    'twoBr',
    'twoBrInbound',
    'twoBrMedia',
    'twoBrFlex',
    'twoBrDen',
    'twoBrDenInbound',
    'juniorTwo',
    'threeBr',
    'threeBrInbound',
    'threeBrMedia',
    'threeBrFlex',
    'threeBrDen',
    'th',
  ];

  const getUnitType = (unitType: string) => {
    switch (unitType) {
      case 'studio':
        return 'Studio';
      case 'jrOneBr':
        return 'JR1BR';
      case 'oneBr':
        return '1BR';
      case 'oneBrInbound':
        return '1BR(I)';
      case 'oneBrDen':
        return '1BR+D';
      case 'oneBrStudy':
        return '1BR+S';
      case 'oneBrMedia':
        return '1BR+M';
      case 'oneBrFlex':
        return '1BR+F';
      case 'oneBrDenInbound':
        return '1BR+D(I)';
      case 'oneBrMediaInbound':
        return '1BR+M(I)';
      case 'twoBr':
        return '2BR';
      case 'twoBrInbound':
        return '2BR(I)';
      case 'twoBrDen':
        return '2BR+D';
      case 'twoBrStudy':
        return '2BR+S';
      case 'twoBrMedia':
        return '2BR+M';
      case 'twoBrFlex':
        return '2BR+F';
      case 'juniorTwo':
        return 'JR2';
      case 'twoBrDenInbound':
        return '2BR+D(I)';
      case 'twoBrMediaInbound':
        return '2BR+M(I)';
      case 'threeBr':
        return '3BR';
      case 'threeBrInbound':
        return '3BR(I)';
      case 'threeBrDen':
        return '3BR+D';
      case 'threeBrMedia':
        return '3BR+M';
      case 'threeBrFlex':
        return '3BR+F';
      case 'th':
        return 'TH';
      default:
        return 'N/A';
    }
  };

  console.log(availableUnitTypes, 'available');
  const isAvailableUnitType = (id: string) => availableUnitTypes.includes(id) || availableUnitTypes.includes(getUnitType(id));

  const duplicateColumns = [
    {
      Header: 'Purchaser Full Name',
      accessor: (rowData: any) => {
        return rowData.purchaser.fullName;
      },
    },
    {
      Header: 'Purchaser Email',
      accessor: (rowData: any) => {
        return rowData.purchaser.email;
      },
    },
    {
      Header: 'Realtor 1',
      accessor: (rowData: any) => {
        if (rowData.worksheets[0].realtor) {
          return rowData.worksheets[0].realtor.fullName;
        } else return '-';
      },
    },
    {
      Header: 'Realtor 1 Email',
      accessor: (rowData: any) => {
        if (rowData.worksheets[0].realtor) {
          return rowData.worksheets[0].realtor.email;
        }
      },
    },
    {
      Header: 'Realtor 1 Unit',
      accessor: (rowData: any) => {
        return rowData.worksheets[0].unit ? rowData.worksheets[0].unit.suite : 'Not Assigned To Unit';
      },
    },
    {
      Header: 'Realtor 1 Created At',
      accessor: (rowData: any) => {
        return convertAllDates(rowData.worksheets[0].createdAt, 'PPpp');
      },
    },
    {
      Header: 'Realtor 2',
      accessor: (rowData: any) => {
        if (rowData.worksheets[1].realtor) {
          return rowData.worksheets[1].realtor.fullName;
        } else return '-';
      },
    },
    {
      Header: 'Realtor 2 Email',
      accessor: (rowData: any) => {
        if (rowData.worksheets[1].realtor) {
          return rowData.worksheets[1].realtor.email;
        } else return '-';
      },
    },
    {
      Header: 'Realtor 2 Unit',
      accessor: (rowData: any) => {
        return rowData.worksheets[1].unit ? rowData.worksheets[1].unit.suite : 'Not Assigned To Unit';
      },
    },
    {
      Header: 'Realtor 2 Created At',
      accessor: (rowData: any) => {
        return convertAllDates(rowData.worksheets[1].createdAt, 'PPpp');
      },
    },
  ];

  const columns = useMemo(() => {
    return [
      {
        Header: 'Rank',
        // getWorksheetSummary only ever returns the top 3 per category, so ranks beyond 3rd are unreachable.
        accessor: (rowData: any, index: number) => {
          if (index === 0) {
            return <Box sx={{ color: '#d1b313', fontSize: '20px' }}>{index + 1}st</Box>;
          } else if (index === 1) {
            return <Box sx={{ color: '#535156', fontSize: '20px' }}>{index + 1}nd</Box>;
          } else {
            return <Box sx={{ color: '#CD7F32', fontSize: '20px' }}>{index + 1}rd</Box>;
          }
        },
      },
      {
        Header: 'Type',
        accessor: (rowData: any) => rowData._id,
      },
      {
        Header: 'Count',
        accessor: (rowData: any) => rowData.count,
      },
    ];
  }, []);

  const optionsColumns = useMemo(() => {
    return [
      ...columns,
      {
        Header: 'Rented',
        accessor: (rowData: any) => {
          return wishlistWorksheets.filter((worksheet: any) =>
            (worksheet.options || []).some((option: any) => option.name === rowData._id && option.rent)
          ).length;
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, wishlistWorksheets]);

  const realtorColumns = useMemo(() => {
    let unitColumns = [
      {
        Header: 'Realtor',
        accessor: (rowData: any) => {
          return <strong>{rowData._id.fullName}</strong>;
        },
      },
      {
        Header: 'Total Wishlist Worksheets',
        accessor: (rowData: any) => <strong>{rowData.count}</strong>,
      },
    ];

    let downloadColumn = {
      Header: 'Download PDF',
      accessor: (rowData: any) => {
        if (rowData._id.fullName === 'Total') return null;
        const realtorWorksheets = wishlistWorksheets.filter((worksheet: any) =>
          rowData._id._id ? worksheet.realtor?._id === rowData._id._id : !worksheet.realtor
        );
        return (
          <Button
            variant="contained"
            color="primary"
            disabled={!realtorWorksheets.length}
            onClick={() => exportRealtorWishlistWorksheetsPdf(realtorWorksheets, rowData._id.fullName, project)}
          >
            Download
          </Button>
        );
      },
    };

    if (wishlistSummary) {
      let checkUnitType = unitTypes.filter((unitType: string) => isAvailableUnitType(unitType));

      let selectedUnitTypes = checkUnitType.map((checkUnit: any) => {
        return {
          Header: getUnitType(checkUnit)!,
          accessor: (rowData: any) => {
            return (
              <Box>
                <strong>{rowData[checkUnit] ? rowData[checkUnit] : ''}</strong>
              </Box>
            );
          },
        };
      });

      return [...unitColumns, ...selectedUnitTypes, downloadColumn];
    } else return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wishlistSummary, availableUnitTypes, wishlistWorksheets]);

  const publicRow = useMemo(() => {
    let publicCounts = unitTypes.reduce((acc: any, unitType: string) => {
      acc[unitType] = 0;
      return acc;
    }, {});

    publicWorksheets.forEach((worksheet: any) => {
      worksheet.wishlistChoices.forEach((choice: any) => {
        let unitTypeKey = unitTypes.find((key: string) => getUnitType(key) === choice.unitType);
        if (unitTypeKey) publicCounts[unitTypeKey] += 1;
      });
    });

    return { _id: { fullName: 'Public (No Realtor)' }, count: publicWorksheets.length, ...publicCounts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicWorksheets]);

  const realtorRows = useMemo(() => {
    if (!wishlistSummary) return [];

    let rows = [...wishlistSummary.realtors, publicRow];

    let totals = unitTypes.reduce((acc: any, unitType: string) => {
      acc[unitType] = rows.reduce((sum: number, realtor: any) => sum + (realtor[unitType] || 0), 0);
      return acc;
    }, {});
    let totalCount = rows.reduce((sum: number, realtor: any) => sum + (realtor.count || 0), 0);

    return [...rows, { _id: { fullName: 'Total' }, count: totalCount, ...totals }];
  }, [wishlistSummary, publicRow]);

  const download = (type: string, data: any) => {
    let checkUnitType = unitTypes.filter((unitType: string) => isAvailableUnitType(unitType));

    let allData = data.map((data: any) => {
      let dataObject = checkUnitType.reduce((obj: any, item: any) => ((obj[item] = data[item]), obj), {});

      return {
        realtor: data._id.fullName,
        totalWorksheets: data.count,
        ...dataObject,
      };
    });

    let excelObject = checkUnitType.reduce((obj: any, item: any) => ((obj[item] = 15), obj), {});
    let pdfObject = checkUnitType.reduce((obj: any, item: any) => ((obj[item] = 200), obj), {});

    let widths = {
      realtor: 15,
      totalWorksheets: 15,
    };

    let pdfWidth = {
      realtor: 200,
      totalWorksheets: 200,
    };

    let excelWidths = { ...widths, ...excelObject };
    let pdfWidths = { ...pdfWidth, ...pdfObject };

    let objectHeaders = checkUnitType.map((data: any) => {
      return {
        label: getUnitType(data),
        id: data,
      };
    });

    let headers = [
      {
        label: 'Realtor',
        id: 'realtor',
      },
      {
        label: 'Total Wishlist Worksheets',
        id: 'totalWorksheets',
      },
    ];

    let allHeaders = [...headers, ...objectHeaders];

    let sheetTitle = `${project.name} - Wishlist Summary`;

    if (type === 'excel') {
      downloadExcel([allData], [allHeaders], [], [[excelWidths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([allData], [allHeaders], [], [pdfWidths], [sheetTitle], sheetTitle);
    }
  };

  const duplicateDownload = (type: string, data: any) => {
    let unitHeaders = [
      {
        label: 'Purchaser Full Name',
        id: 'purchaserFullName',
      },
      {
        label: 'Purchaser Email',
        id: 'purchaserEmail',
      },
      {
        label: 'Realtor 1',
        id: 'realtorOne',
      },
      {
        label: 'Realtor 1 Email',
        id: 'realtorOneEmail',
      },
      {
        label: 'Realtor 1 Unit',
        id: 'realtorOneUnit',
      },
      {
        label: 'Realtor 1 Created At',
        id: 'realtorOneCreatedAt',
      },
      {
        label: 'Realtor 2',
        id: 'realtorTwo',
      },
      {
        label: 'Realtor 2 Email',
        id: 'realtorTwoEmail',
      },
      {
        label: 'Realtor 2 Unit',
        id: 'realtorTwoUnit',
      },
      {
        label: 'Realtor 2 Created At',
        id: 'realtorTwoCreatedAt',
      },
    ];

    let widths = {
      purchaserFullName: 15,
      purchaserEmail: 15,
      realtorOne: 15,
      realtorOneEmail: 15,
      realtorOneUnit: 15,
      realtorOneCreatedAt: 15,
      realtorTwo: 15,
      realtorTwoEmail: 15,
      realtorTwoUnit: 15,
      realtorTwoCreatedAt: 15,
    };

    let pdfWidths = {
      purchaserFullName: 200,
      purchaserEmail: 200,
      realtorOne: 200,
      realtorOneEmail: 200,
      realtorOneUnit: 200,
      realtorOneCreatedAt: 200,
      realtorTwo: 200,
      realtorTwoEmail: 200,
      realtorTwoUnit: 200,
      realtorTwoCreatedAt: 200,
    };

    let updatedData = data.map((data: any) => {
      return {
        purchaserFullName: data.purchaser.fullName,
        purchaserEmail: data.purchaser.email,
        realtorOne: data.worksheets[0].realtor ? data.worksheets[0].realtor.fullName : '-',
        realtorOneEmail: data.worksheets[0].realtor ? data.worksheets[0].realtor.email : '-',
        realtorOneUnit: data.worksheets[0].unit ? data.worksheets[0].unit.suite : 'Not Assigned To Unit',
        realtorOneCreatedAt: convertAllDates(data.worksheets[0].createdAt, 'PPpp'),
        realtorTwo: data.worksheets[1].realtor ? data.worksheets[1].realtor.fullName : '-',
        realtorTwoEmail: data.worksheets[1].realtor ? data.worksheets[1].realtor.email : '-',
        realtorTwoUnit: data.worksheets[1].unit ? data.worksheets[1].unit.suite : 'Not Assigned To Unit',
        realtorTwoCreatedAt: convertAllDates(data.worksheets[1].createdAt, 'PPpp'),
      };
    });

    let sheetTitle = `${project.name} - Duplicate Worksheets`;

    if (type === 'excel') {
      downloadExcel([updatedData], [unitHeaders], [], [[widths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([updatedData], [unitHeaders], [], [pdfWidths], [sheetTitle], sheetTitle);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography gutterBottom variant="h2">
        <strong>Worksheets Report</strong>
      </Typography>
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            '-webkit-transform': 'translate(-50%, -50%)',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <LoadingLogo />
        </Box>
      ) : wishlistSummary ? (
        <>
          <Box>
            <Grid container spacing={3}>
              <Grid item md={3} sx={{ mb: 8 }}>
                <Box sx={{ p: 2, backgroundColor: '#2e453d', color: '#fff' }}>
                  <strong>Models</strong>
                </Box>
                <StandardTable columns={columns} data={wishlistSummary.modelTypes.slice(0, 3)} minWidth={false} size="small" borderRadius={0} />
              </Grid>
              <Grid item md={3} sx={{ mb: 8 }}>
                <Box sx={{ p: 2, backgroundColor: '#2e453d', color: '#fff' }}>
                  <strong>Unit Types</strong>
                </Box>
                <StandardTable
                  columns={columns}
                  data={wishlistSummary.unitTypes.filter((unitType: any) => isAvailableUnitType(unitType._id)).slice(0, 3)}
                  minWidth={false}
                  size="small"
                  borderRadius={0}
                />
              </Grid>
              <Grid item md={3} sx={{ mb: 8 }}>
                <Box sx={{ p: 2, backgroundColor: '#2e453d', color: '#fff' }}>
                  <strong>Level</strong>
                </Box>
                <StandardTable columns={columns} data={wishlistSummary.levels.slice(0, 3)} minWidth={false} size="small" borderRadius={0} />
              </Grid>
              <Grid item md={3} sx={{ mb: 8 }}>
                <Box sx={{ p: 2, backgroundColor: '#2e453d', color: '#fff' }}>
                  <strong>Exposures</strong>
                </Box>
                <StandardTable columns={columns} data={wishlistSummary.exposures.slice(0, 3)} minWidth={false} size="small" borderRadius={0} />
              </Grid>
              <Grid item md={3} sx={{ mb: 8 }}>
                <Box sx={{ p: 2, backgroundColor: '#2e453d', color: '#fff' }}>
                  <strong>Options</strong>
                </Box>
                <StandardTable
                  columns={optionsColumns}
                  data={wishlistSummary.options.slice(0, 3)}
                  minWidth={false}
                  size="small"
                  borderRadius={0}
                />
              </Grid>
            </Grid>
          </Box>
          <Typography sx={{ mt: 3 }} variant={'h5'} gutterBottom>
            Worksheets
          </Typography>
          <Box>
            <StandardTable download={download} columns={realtorColumns} data={realtorRows} />
          </Box>
          {duplicates.length ? (
            <>
              <Typography sx={{ mt: 2 }} variant={'h5'} gutterBottom>
                Duplicate Worksheets
              </Typography>
              <Box>
                <StandardTable download={duplicateDownload} columns={duplicateColumns} data={duplicates} />
              </Box>
            </>
          ) : null}
        </>
      ) : null}
    </Box>
  );
};

const UNITS = gql`
  query unitMany($filter: FilterFindManyUnitInput) {
    unitMany(filter: $filter, limit: 10000) {
      _id
      unitType
    }
  }
`;

const WISHLIST_WORKSHEETS = gql`
  query getWorksheets($search: String, $project: MongoID!, $type: String!, $page: Int!, $perPage: Int!) {
    getWorksheets(search: $search, project: $project, type: $type, page: $page, perPage: $perPage) {
      worksheets {
        _id
        unit {
          _id
          suite
        }
        realtor {
          _id
          fullName
          email
          directPhone
          brokerage
          brokeragePhone
          brokerageFax
          streetAddress
          city
          province
          postalCode
        }
        purchasers {
          _id
          firstName
          lastName
          email
          streetAddress
          city
          province
          country
          postalCode
          purchaserType
          employer
          occupation
          primaryPhone
          dob
          sin
          idType
          idJurisdiction
        }
        wishlistChoices {
          modelTypes
          unitType
          level
          exposure
        }
        options {
          name
          amount
          purchaseAmount
          rent
        }
        notes
        chequeImages {
          name
          _id
          getUrl
        }
      }
    }
  }
`;

const DUPLICATES = gql`
  query getDuplicates($project: MongoID!) {
    getDuplicates(project: $project) {
      purchaser {
        fullName
        email
        primaryPhone
      }
      worksheets {
        realtor {
          fullName
          email
        }
        unit {
          suite
        }
        createdAt
      }
    }
  }
`;

const WORKSHEETS = gql`
  query getWorksheetSummary($project: MongoID!) {
    getWorksheetSummary(project: $project) {
      unitTypes {
        _id
        count
      }
      modelTypes {
        _id
        count
      }
      levels {
        _id
        count
      }
      exposures {
        _id
        count
      }
      options {
        _id
        count
      }
      realtors {
        _id {
          _id
          fullName
        }
        count
        studio
        oneBr
        oneBrInbound
        oneBrDen
        oneBrDenInbound
        juniorTwo
        twoBr
        twoBrInbound
        twoBrDen
        twoBrDenInbound
        threeBr
        threeBrInbound
        threeBrDen
        th
      }
    }
  }
`;

export default Worksheets;
